import { useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { BoxOverlay, boxesFromConfiguration } from '@/components/overlay/BoxOverlay'
import { FxAlert, FxButton, FxPanel, FxSpinner } from '@/components/fx'
import { templatesApi } from '@/lib/api'
import { pageDisplayName, templateDetecting, templateSettling } from '@/types/domain'
import type { DetectionResult } from '@/types/domain'

/**
 * "Wir haben 27 Felder erkannt — bitte prüfen Sie die Zuordnung."
 *
 * Read-only on purpose. Drawing, dragging and field binding are the template
 * editor's job and stay in the Flutter app until that screen moves; what
 * Phase 1 needs is for someone to *see* what detection produced and say yes.
 * So the boxes are drawn, the detector's remark sits on top, and the two
 * answers are "passt" and "später anpassen".
 */
/** How long a PDF is given to become pages before the poll stops. The
 *  converter records a failed conversion on the job, not on the template, so
 *  an empty `pageList` is indistinguishable from one that never arrives. */
const CONVERT_DEADLINE_MS = 5 * 60_000

export function ConfirmTemplatePage() {
  const { templateId = '' } = useParams()
  const navigate = useNavigate()
  const detection = (useLocation().state as { detection?: DetectionResult } | null)?.detection
  const [pageIndex, setPageIndex] = useState(0)
  const openedAt = useRef(Date.now())

  const template = useQuery({
    queryKey: ['template', templateId],
    queryFn: () => templatesApi.get(templateId),
    // A PDF's pages are rendered by the converter after the upload answered,
    // and field detection runs there too, for a PDF and an image alike. So
    // the template arrives without pages or without boxes and fills in. Poll
    // until the pages are out of `converting` and the template out of
    // `detecting`, then stop.
    refetchInterval: (query) => {
      // A failed query holds no data, so polling on "no data yet" would keep
      // asking every two seconds behind the terminal "Formular nicht
      // gefunden" this screen renders in the same breath.
      if (query.state.status === 'error') return false
      const data = query.state.data
      if (!data) return 2000
      if (!templateSettling(data)) return false
      // A conversion that never completes must not poll for as long as the
      // tab is open; past the deadline the screen says so instead.
      return Date.now() - openedAt.current < CONVERT_DEADLINE_MS ? 2000 : false
    },
  })

  const pages = template.data?.pageList ?? []
  const page = pages[Math.min(pageIndex, Math.max(pages.length - 1, 0))]
  const boxes = useMemo(() => (page ? boxesFromConfiguration(page.boxConfiguration) : []), [page])
  const fieldTotal = pages.reduce((sum, p) => sum + p.boxConfiguration.length, 0)

  if (template.isPending) {
    return (
      <main className="grid min-h-full place-items-center p-8">
        <FxSpinner label="Formular wird geladen …" />
      </main>
    )
  }

  if (template.isError || !template.data) {
    return (
      <main className="mx-auto w-full max-w-[720px] p-8">
        <FxAlert tone="danger" title="Formular nicht gefunden">
          Das Formular konnte nicht geladen werden.
        </FxAlert>
      </main>
    )
  }

  const settling = templateSettling(template.data)
  const detecting = templateDetecting(template.data)
  const converting = settling && !detecting
  const convertGaveUp = settling && Date.now() - openedAt.current >= CONVERT_DEADLINE_MS
  const failedPages = pages.filter((p) => p.status === 'failed')

  return (
    <main className="mx-auto w-full max-w-[1100px] space-y-8 p-8">
      <header className="space-y-2">
        <h1 className="text-page-title">{template.data.title || 'Ihr Formular'}</h1>
        {detecting ? (
          <p className="text-body text-text-muted">Wir suchen gerade die Felder in Ihrem Formular …</p>
        ) : fieldTotal > 0 ? (
          <p className="text-body text-text-muted">
            Wir haben <strong>{fieldTotal} Felder</strong> erkannt — bitte prüfen Sie die Zuordnung.
          </p>
        ) : (
          <p className="text-body text-text-muted">
            Bitte prüfen Sie die Feldzuordnung dieses Formulars.
          </p>
        )}
      </header>

      {detection?.error && (
        <FxAlert tone="warning" title="Die Felderkennung ist nicht durchgelaufen">
          {detection.error} Das Formular ist gespeichert — die Felder können Sie im Editor selbst
          einzeichnen.
        </FxAlert>
      )}

      {detection?.remark && <FxAlert tone="info" title="Hinweis des Modells">{detection.remark}</FxAlert>}

      {converting && !convertGaveUp && (
        <FxAlert tone="info" title="Seiten werden noch erzeugt">
          Bei einem PDF rendern wir die Seiten im Hintergrund. Die Felder erscheinen, sobald das
          fertig ist.
        </FxAlert>
      )}

      {detecting && !convertGaveUp && (
        <FxAlert tone="info" title="Felder werden erkannt …">
          Das dauert meist unter einer Minute. Die Felder erscheinen hier von selbst, sobald die
          Erkennung fertig ist — Sie müssen nichts neu laden oder erneut hochladen.
        </FxAlert>
      )}

      {convertGaveUp && !detecting && (
        <FxAlert tone="danger" title="Die Seiten sind nicht fertig geworden">
          Das Umwandeln des PDFs in einzelne Seiten dauert ungewöhnlich lange oder ist
          fehlgeschlagen. Das Formular ist gespeichert — laden Sie die Seiten notfalls als Bild
          hoch.
        </FxAlert>
      )}

      {convertGaveUp && detecting && (
        <FxAlert tone="danger" title="Die Felderkennung ist nicht fertig geworden">
          Sie dauert ungewöhnlich lange oder ist abgebrochen. Das Formular ist gespeichert — die
          Felder können Sie im Editor selbst einzeichnen.
        </FxAlert>
      )}

      {failedPages.length > 0 && (
        <FxAlert tone="danger" title="Eine Seite konnte nicht gerendert werden">
          {failedPages.map(pageDisplayName).join(', ')} — bitte die Datei noch einmal hochladen.
        </FxAlert>
      )}

      {pages.length > 1 && (
        <nav className="flex flex-wrap gap-2" aria-label="Seiten">
          {pages.map((candidate, index) => (
            <FxButton
              key={candidate.page}
              variant={index === pageIndex ? 'primary' : 'outlined'}
              onClick={() => setPageIndex(index)}
            >
              {pageDisplayName(candidate)}
              <span className="text-text-muted">· {candidate.boxConfiguration.length}</span>
            </FxButton>
          ))}
        </nav>
      )}

      {page && page.status === 'ready' && (
        <BoxOverlay imagePath={page.imagePath} boxes={boxes} />
      )}

      {/* Not while detection is still running: the page is ready before its
          boxes are, and "no field found" during that minute read as a
          failed detection. */}
      {page && boxes.length === 0 && page.status === 'ready' && !detecting && (
        <FxAlert tone="warning" title="Auf dieser Seite wurde kein Feld gefunden">
          Bei einem Formular ohne gedruckte Kästchen oder Linien findet die geometrische Erkennung
          nichts. Die KI-Erkennung kommt hier oft weiter — oder Sie zeichnen die Felder selbst ein.
        </FxAlert>
      )}

      <FxPanel title="Stimmt die Zuordnung?">
        <div className="flex flex-wrap gap-4">
          {/* The separate "first scan" screen is gone: the funnel uploads and
              reads the scan itself, so both answers here continue into the
              template rather than into an upload form of their own. */}
          <FxButton onClick={() => navigate(`/templates/${templateId}`)}>
            Passt — zum Formular
          </FxButton>
          <FxButton variant="outlined" onClick={() => navigate('/')}>
            Später anpassen
          </FxButton>
        </div>
        <p className="mt-4 text-small text-text-muted">
          Felder verschieben, umbenennen und binden können Sie jederzeit im Formular-Editor.
        </p>
      </FxPanel>
    </main>
  )
}
